import { Card } from 'components/Card';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { clone, cloneDeep, isEqual, sortBy, without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doAseSetupTemplateClosed,
  doAseSetupTemplateDelete,
  doAseSetupTemplateSave,
  doToast,
  eventAssessmentTemplateFieldScreenDone,
  eventAssessmentTemplateQuestionScreenDone,
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { AssessmentPdf, AssessmentTemplate } from 'store/remote/types';
import { useAlert } from 'hooks/useAlert';
import {
  selectAseSetupLibraryQuestions,
  selectAseSetupLibraryTemplateHasQuestions,
  selectAseSetupTplTemplateId,
  selectRemoteAssessmentFlags,
  selectRemoteAssessmentPdfs,
  selectRemoteAssessmentSchemes,
  selectRemoteAssessmentSignatories,
  selectRemoteAssessmentTemplate,
  selectRemoteAssessmentTemplateDefaultCosts,
  selectRemoteAssessmentValidityTimescales,
  selectRemoteComplianceSubtypesWithType,
  selectRemoteCustomers,
  selectRemoteOrganisation,
} from 'store/remote/selectors';
import { useSelector } from 'hooks/useSelector';
import { CardRow } from 'components/CardRow';
import { animateNext } from 'lib/animateNext';
import {
  ActionTimescaleFrom,
  AseTemplateOptionPdfActionsControls,
} from 'types/Assessments';
import { AssessmentQuestionType, TimescalePeriod, UID } from 'types/types';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Platform } from 'react-native';
import { Icon, ICONS } from 'components/Icon';
import { TimescaleField } from 'components/Field/TimescaleField';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { arrayJoin, filterArrayEmpty, randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { pluralise } from 'lib/pluralise';
import { Txt } from 'components/Txt';
import { arrayHasSameMembers } from 'lib/arrayHasSameMembers/arrayHasSameMembers';
import { selectUserIsSupport } from 'store/auth/selectors';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { TextField } from 'components/Field/TextField';
import { OptionField } from 'components/Field/OptionField';
import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import { Freeze } from 'react-freeze';
import { smartText } from 'lib/str';
import { FieldsListItem } from './ListItems/FieldsListItem';
import { CostsScreen } from './CostsScreen';
import { PageHeader } from 'components/PageHeader';
import { ScrollView } from 'components/ScrollView';
import { GapSmall } from 'components/GapSmall';
import { Gap } from 'components/Gap';

type Tabs = 'general' | 'pdf' | 'pricing';

function getNewTemplate({
  complianceSubtypeId,
  pdfs,
}: {
  complianceSubtypeId: number;
  pdfs: Record<string, AssessmentPdf>;
}): AssessmentTemplate {
  return {
    id: 0,
    name: '',
    description: '',
    title: '',
    enabled: false,
    complianceSubtypeId,
    schemeId: 1,
    pdfId:
      Object.values(pdfs).find((p) => !!p.default)?.id ||
      Object.values(pdfs)[0]!.id,
    approveOnSubmit: false,
    assessorApproval: false,
    actionTimescaleFrom: ActionTimescaleFrom.assessedDate,
    validityModifierId: null,
    validityTimescale: [1, TimescalePeriod.year],
    requireAllAnswers: false,
    questionComments: false,
    addCustomActions: true,
    addCustomControls: true,
    defaultScope: '',
    specificationConformsTo: '',
    flagIds: [],
    signatoryIds: [],
    limitOrganisations: false,
    visibleToOrganisationIds: [],
    labelApproved: '',
    pdfActionPlan: null,
    pdfActionsControls: AseTemplateOptionPdfActionsControls.actionsControls,
    pdfIncludeCertificate: true,
    pdfPagePhotos: true,
    pdfContents: true,
    showDashChecklistItems: false,
    showActionHistory: true,
    showCancelledActions: true,
    showControlHistory: true,
    showCancelledControls: true,
    pdfNewFieldInfoPage: true,
    pdfNewFooter: true,
    pdfNewFont: false,
    pdfH1Numbering: false,
    pdfSignatories: true,
    pdfSignatoriesText: '',
    pdfSignatoriesTitle: '',
    editAssetName: false,
    editAssetAddress: false,
    editAssetAvatar: false,
    editAssetCustomReference: false,
    pdfIntroduction: '',
    pdfAppendix: '',
    pdfBody: '',
    pdfActionPlanText: '',
    pdfShowActionSeverities: true,
    pdfShowActionTimescales: true,
    pdfCoverAssessingOrganisation: true,
    allowMultipleAssessors: false,
    costs: {},
    fieldsConfig: {},
    colourHeader1: '',
    colourHeader2: '',
    colourHeader3: '',
    colourHeaderCover: '',
    ownerApprovalOnly: false,
  };
}

export const TemplateScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPTEMPLATE>
> = ({ navigation, route }) => {
  const { newData } = route.params;
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const alert = useAlert();
  const forceUpdate = useForceUpdate();
  const isSupport = useSelector(selectUserIsSupport);

  const [activeTab, setActiveTabActual] = React.useState<Tabs>('general');
  const openedTabsRef = React.useRef<Tabs[]>(['general']);

  const setActiveTab = React.useCallback((newActiveTab: Tabs) => {
    if (!openedTabsRef.current.includes(newActiveTab)) {
      openedTabsRef.current.push(newActiveTab);
    }
    setActiveTabActual(newActiveTab);
  }, []);

  const eventKeySelectOrganisationFields = React.useRef(randomKey());
  const eventKeySelectOrganisation = React.useRef(randomKey());
  const eventKeySelectQuestions = React.useRef(randomKey());
  const eventKeyFields = React.useRef(randomKey());

  const organisation = useSelector(selectRemoteOrganisation)!;
  const customers = useSelector(selectRemoteCustomers)!;
  const allOrganisations = [
    organisation,
    ...sortBy(Object.values(customers), 'name'),
  ];
  const questions = useSelector(selectAseSetupLibraryQuestions)!;
  const allOrganisationIds = allOrganisations.map((o) => o.id);
  const complianceSubtypes = useSelector(
    selectRemoteComplianceSubtypesWithType,
  )!;
  const pdfs = useSelector(selectRemoteAssessmentPdfs)!;
  const schemes = useSelector(selectRemoteAssessmentSchemes)!;
  const flags = useSelector(selectRemoteAssessmentFlags)!;
  const signatories = useSelector(selectRemoteAssessmentSignatories)!;
  const validityTimescales = useSelector(
    selectRemoteAssessmentValidityTimescales,
  )!;
  const templateDefaultCosts = useSelector(
    selectRemoteAssessmentTemplateDefaultCosts,
  );

  const templateId = useSelector(selectAseSetupTplTemplateId);
  const initIdRef = React.useRef(templateId);

  const defaultNewTemplate = React.useMemo(
    () =>
      getNewTemplate({
        complianceSubtypeId: newData?.complianceSubtypeId || 0,
        pdfs,
      }),
    [newData?.complianceSubtypeId, pdfs],
  );

  const template =
    useSelector((state) =>
      selectRemoteAssessmentTemplate(state, templateId || -1),
    ) || defaultNewTemplate;

  const currentQuestionIds = useSelector((state) =>
    selectAseSetupLibraryTemplateHasQuestions(state, template.id),
  );

  const [currentlyEditing, setCurrentlyEditing] = React.useState<
    | undefined
    | 'schemeId'
    | 'actionTimescaleFrom'
    | 'flagIds'
    | 'signatoryIds'
    | 'validityPeriodId'
    | 'pdfId'
    | 'visibleToOrganisationIds'
    | 'editAsset'
  >();

  const isDirty = React.useRef(false);
  const data = React.useRef(cloneDeep(template));
  const dataQuestionIds = React.useRef(clone(currentQuestionIds));
  const checkIsDirty = React.useCallback(
    () =>
      !isEqual(template, data.current) ||
      !arrayHasSameMembers(currentQuestionIds, dataQuestionIds.current),
    [currentQuestionIds, template],
  );

  const actionTimescaleFromOptions: Record<
    ActionTimescaleFrom,
    { id: ActionTimescaleFrom; name: string }
  > = {
    [ActionTimescaleFrom.assessedDate]: {
      id: ActionTimescaleFrom.assessedDate,
      name: 'Assessed Date',
    },
    [ActionTimescaleFrom.approvedDate]: {
      id: ActionTimescaleFrom.approvedDate,
      name: 'Approved Date',
    },
  };

  // Reset data if ID changes, then check isDirty
  if (data.current.id !== template.id) {
    data.current = cloneDeep(template);
    dataQuestionIds.current = clone(currentQuestionIds);
  }
  isDirty.current = checkIsDirty();

  const { questionIds, checklistIds } = dataQuestionIds.current.reduce<{
    questionIds: number[];
    checklistIds: number[];
  }>(
    (acc, questionId) => {
      if (questions[questionId]?.type === AssessmentQuestionType.checklist) {
        acc.checklistIds.push(questionId);
      } else {
        acc.questionIds.push(questionId);
      }
      return acc;
    },
    { questionIds: [], checklistIds: [] },
  );

  const complianceSubtype = complianceSubtypes[
    data.current.complianceSubtypeId
  ] || {
    id: 0,
    name: 'Please Choose Compliance Type',
    complianceTypeId: 0,
    complianceTypeName: '',
  };

  const flagNames = sortBy(Object.values(flags), 'name')
    .filter((f) => data.current.flagIds.includes(f.id))
    .map((item) => item.name)
    .join(', ');

  const signatoryNames = sortBy(Object.values(signatories), 'ordering')
    .filter((f) => data.current.signatoryIds.includes(f.id))
    .map((item) => item.name)
    .join(', ');

  const validityTimescale = data.current.validityModifierId
    ? validityTimescales[data.current.validityModifierId]
    : undefined;

  const certificate = schemes[data.current.schemeId]!;
  const pdf = pdfs[data.current.pdfId]!;
  const visibleToOrganisationIds = data.current.visibleToOrganisationIds.filter(
    (oId) => allOrganisationIds.includes(oId),
  );

  const editAssetString = arrayJoin(
    [
      data.current.editAssetName ? 'Name' : null,
      data.current.editAssetAddress ? 'Address' : null,
      data.current.editAssetAvatar ? 'Photo' : null,
      data.current.editAssetCustomReference ? 'Custom Reference' : null,
    ].filter(filterArrayEmpty),
    ', ',
    ' and ',
  );

  const shouldReload = React.useCallback(() => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  }, [checkIsDirty, forceUpdate]);

  const handleChangeWithName = React.useCallback(
    <T extends keyof typeof data.current>(
      name: T,
      value: (typeof data.current)[T],
      doForceUpdate: boolean = false,
    ) => {
      data.current = {
        ...data.current,
        [name]: value,
      };
      if (doForceUpdate) {
        forceUpdate();
      } else {
        shouldReload();
      }
    },
    [forceUpdate, shouldReload],
  );

  const handleChangeWithNameWithForceUpdate = React.useCallback(
    <T extends keyof typeof data.current>(
      name: T,
      value: (typeof data.current)[T],
    ) => handleChangeWithName(name, value, true),
    [handleChangeWithName],
  );

  const colourRegExp = /^#[0-9A-F]{6}$/i;
  const colourWithHash = (value: string) =>
    value ? `#${`${value}`.replace('#', '')}` : '';

  const handleColourChange = <
    T extends keyof Pick<
      typeof data.current,
      'colourHeader1' | 'colourHeader2' | 'colourHeader3' | 'colourHeaderCover'
    >,
  >(
    name: T,
    value: (typeof data.current)[T],
  ) => handleChangeWithName(name, colourWithHash(value), true);

  const returnValidColour = (colours: string[]): string | undefined => {
    const found = colours.find(
      (colour) => colour && colourRegExp.test(colourWithHash(colour)),
    );
    return found ? colourWithHash(found) : undefined;
  };

  const handleSavePress = React.useCallback(
    ({ closeModal }: { closeModal: boolean } = { closeModal: false }) => {
      const errors: string[] = [];
      if (!data.current.complianceSubtypeId) {
        errors.push('choose a type');
      }
      if (!data.current.name.trim()) {
        errors.push('enter a name');
      }
      if (errors.length) {
        alert({
          text: `Please ${arrayJoin(errors, ', ', ' and ')}`,
        });
        return;
      }
      if (isDirty.current) {
        dispatch(
          doAseSetupTemplateSave({
            id: templateId || null,
            data: { ...data.current, questionIds: dataQuestionIds.current },
            close: closeModal,
          }),
        );
      } else if (closeModal) {
        dispatch(NAVIGATE_MODAL_BACK());
      } else {
        dispatch(doToast('No Changes to Apply'));
      }
    },
    [alert, dispatch, templateId],
  );

  const handleToggleFlag = (flagId: string) => {
    if (data.current.flagIds.includes(flagId)) {
      data.current.flagIds = without(data.current.flagIds, flagId);
    } else {
      data.current.flagIds.push(flagId);
    }
    forceUpdate();
  };

  const handleToggleSignatory = (signatoryId: UID) => {
    if (data.current.signatoryIds.includes(signatoryId)) {
      data.current.signatoryIds = without(
        data.current.signatoryIds,
        signatoryId,
      );
    } else {
      data.current.signatoryIds.push(signatoryId);
    }
    forceUpdate();
  };

  const handlePressSetQuestions = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPTEMPLATEQUESTIONLIST,
        params: {
          questionIds: [...questionIds, ...checklistIds],
          eventKey: eventKeySelectQuestions.current,
        },
      }),
    );
  };

  const handlePressSetFields = React.useCallback(
    (organisationId: UID) => {
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.ASESETUPTEMPLATEFIELDS,
          params: {
            eventKey: eventKeyFields.current,
            organisationId,
            fieldsConfig: data.current.fieldsConfig,
          },
        }),
      );
    },
    [dispatch],
  );

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSavePress({ closeModal: true }),
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSavePress]);

  const handleDelete = () =>
    dispatch(doAseSetupTemplateDelete({ id: template.id }));

  const handleChangeActionPlan = React.useCallback(
    (newValue: (typeof data.current.pdfActionPlan)[]) => {
      data.current.pdfActionPlan = newValue[0] || null;
      forceUpdate();
    },
    [forceUpdate],
  );

  React.useEffect(() => {
    const events = [
      doAddEvent(
        eventAssessmentTemplateQuestionScreenDone,
        ({ payload: { key, value } }) => {
          if (key === eventKeySelectQuestions.current) {
            dataQuestionIds.current = value;
            forceUpdate();
          }
        },
      ),
      doAddEvent(
        eventAssessmentTemplateFieldScreenDone,
        ({ payload: { key, value } }) => {
          if (key === eventKeyFields.current) {
            data.current.fieldsConfig = value;
            forceUpdate();
          }
        },
      ),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch, forceUpdate]);

  const handleChangeCost = React.useCallback(
    (newCosts: AssessmentTemplate['costs']) => {
      data.current.costs = cloneDeep(newCosts);
      shouldReload();
    },
    [shouldReload],
  );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeySelectOrganisationFields.current) {
          const customerId = event.payload.stringValues[0];
          if (customerId) {
            handlePressSetFields(customerId);
          }
        } else if (event.payload.key === eventKeySelectOrganisation.current) {
          data.current = {
            ...data.current,
            visibleToOrganisationIds: event.payload.stringValues,
          };
          forceUpdate();
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [forceUpdate, handlePressSetFields]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  React.useEffect(() => {
    if (initIdRef.current && !templateId) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (initIdRef.current !== templateId) {
      initIdRef.current = templateId;
    }
  }, [dispatch, templateId]);

  React.useEffect(() => {
    return () => {
      dispatch(doAseSetupTemplateClosed());
    };
  }, [dispatch]);

  return (
    <Page
      header={
        <>
          <PageHeader
            title={complianceSubtype.name}
            onPressCancelButton={handleClose}
            onPressSaveButton={
              templateId || isDirty.current
                ? () => handleSavePress({ closeModal: true })
                : null
            }
            menuRight={
              template.id
                ? {
                    iconButtonConfig: {
                      icon: ICONS.EllipsisLight,
                    },
                    options: [
                      isSupport
                        ? {
                            text: 'Apply Changes',
                            onPress: () =>
                              handleSavePress({ closeModal: false }),
                          }
                        : null,
                      {
                        text: 'Delete Template',
                        onPress: handleDelete,
                        destructive: true,
                      },
                    ].filter(filterArrayEmpty),
                  }
                : undefined
            }
          />
          <ButtonToggleGroup
            buttons={
              isSupport
                ? [
                    { text: 'General', key: 'general' },
                    { text: 'PDF', key: 'pdf' },
                    { text: 'Pricing', key: 'pricing', rbs: true },
                  ]
                : [
                    { text: 'General', key: 'general' },
                    { text: 'PDF', key: 'pdf' },
                  ]
            }
            onActiveChange={(newActive) => setActiveTab(newActive)}
            active="general"
            containerStyle={{
              marginHorizontal: theme.padding,
              marginBottom: theme.padding,
            }}
          />
        </>
      }
    >
      {openedTabsRef.current.includes('general') && (
        <Freeze freeze={activeTab !== 'general'}>
          <ScrollView>
            <Gap>
              <Card
                label="Name & Description"
                explanation="The name and description is used internally by your organisation; it does not appear on reports"
              >
                <TextAreaField
                  name="name"
                  onChangeWithName={handleChangeWithName}
                  placeholder="Name"
                  value={data.current.name}
                  onEsc={handleClose}
                  autoFocusWeb
                  autoFocusNative={!data.current.id}
                  autoFocusDelay
                />
                <TextAreaField
                  name="description"
                  onChangeWithName={handleChangeWithName}
                  placeholder="Description"
                  value={data.current.description}
                  onEsc={handleClose}
                />
              </Card>
              <Card
                label="Title"
                explanation={`The title appears on reports. Leave blank to use: ‘${complianceSubtype.name}’`}
              >
                <TextAreaField
                  name="title"
                  onChangeWithName={handleChangeWithName}
                  placeholder={complianceSubtype.name}
                  value={data.current.title}
                  onEsc={handleClose}
                />
              </Card>

              <SeparatorHorizontal />

              <GapSmall>
                <Card label="Fields">
                  <FieldsListItem
                    config={data.current.fieldsConfig[organisation.id]}
                    organisationId={organisation.id}
                    onPress={() => handlePressSetFields(organisation.id)}
                  />
                  {sortBy(
                    Object.values(customers).filter(
                      (c) =>
                        data.current.fieldsConfig[c.id] &&
                        data.current.fieldsConfig[c.id]?.action !== 'delete',
                    ),
                    'name',
                  ).map((customer) => (
                    <FieldsListItem
                      key={customer.id}
                      config={data.current.fieldsConfig[customer.id]}
                      organisationId={customer.id}
                      onPress={() => handlePressSetFields(customer.id)}
                    />
                  ))}
                </Card>
                {!!Object.keys(customers).length && (
                  <Card>
                    <CardRow
                      style={{ flex: 1 }}
                      label="Customise fields for a customer..."
                      labelColor={theme.colors.blue}
                      icon={ICONS.PlusLight}
                      iconColor={theme.colors.blue}
                      onPress={() =>
                        dispatch(
                          NAVIGATE_MODAL({
                            screen: ModalScreens.SELECT,
                            params: {
                              title: 'Organisations',
                              searchable: true,
                              eventKey:
                                eventKeySelectOrganisationFields.current,
                              options: sortBy(
                                Object.values(customers),
                                'name',
                              ).map((customer) => ({
                                name: customer.name,
                                internalValue: customer.id,
                                groupName:
                                  data.current.fieldsConfig[customer.id] &&
                                  data.current.fieldsConfig[customer.id]
                                    ?.action !== 'delete'
                                    ? 'Already Customised'
                                    : '',
                              })),
                            },
                          }),
                        )
                      }
                    />
                  </Card>
                )}
              </GapSmall>

              <GapSmall>
                <Card label="Checklists and Questions">
                  <CardRow
                    navigationType="input-radio"
                    onPress={handlePressSetQuestions}
                    childrenStyle={{
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Icon
                      source={ICONS.CircleCSolid}
                      color={theme.colors.checklist}
                      style={{ marginRight: theme.padding / 2 }}
                    />
                    <Txt>
                      {!!checklistIds.length && (
                        <Txt weight="bold">{checklistIds.length} </Txt>
                      )}
                      {pluralise(
                        checklistIds.length,
                        'Checklist',
                        'Checklists',
                        'No Checklists',
                      )}
                    </Txt>
                    <Icon
                      source={ICONS.CircleQSolid}
                      color={theme.colors.question}
                      style={{
                        marginRight: theme.padding / 2,
                        marginLeft: theme.padding,
                      }}
                    />
                    <Txt>
                      {!!questionIds.length && (
                        <Txt weight="bold">{questionIds.length} </Txt>
                      )}
                      {pluralise(
                        questionIds.length,
                        'Question',
                        'Questions',
                        'No Questions',
                      )}
                    </Txt>
                  </CardRow>
                </Card>
              </GapSmall>

              <SeparatorHorizontal />

              <Card label="Scheme / Certificate">
                {currentlyEditing === 'schemeId' ? (
                  <Card
                    expandedInline
                    expandedInlineFirst
                    expandedInlineLast
                    marginHorizontal={false}
                  >
                    {sortBy(Object.values(schemes), 'ordering').map((item) => (
                      <CardRow
                        key={item.id}
                        label={item.name}
                        radioValue={data.current.schemeId === item.id}
                        onPress={() => {
                          data.current.schemeId = item.id;
                          setCurrentlyEditing(undefined);
                        }}
                      />
                    ))}
                  </Card>
                ) : (
                  <CardRow
                    label={certificate.name}
                    navigationType="input-radio"
                    onPress={() =>
                      animateNext(() => setCurrentlyEditing('schemeId'))
                    }
                  />
                )}
              </Card>

              <Card label="Specification Conforms To">
                <TextAreaField
                  name="specificationConformsTo"
                  value={data.current.specificationConformsTo}
                  onChangeWithName={handleChangeWithName}
                  placeholder="Our own internal quality system"
                  onEsc={handleClose}
                />
              </Card>

              <Card label="Scope">
                <TextAreaField
                  name="defaultScope"
                  value={data.current.defaultScope}
                  onChangeWithName={handleChangeWithName}
                  placeholder="Only to the building specified"
                  onEsc={handleClose}
                />
              </Card>

              <Card
                label="Flags"
                labelLinkText="Manage Flags"
                onLabelLinkPress={
                  currentlyEditing !== 'flagIds'
                    ? () =>
                        dispatch(
                          NAVIGATE_MODAL({
                            screen: ModalScreens.AseSetupFlags,
                            params: {},
                          }),
                        )
                    : null
                }
                labelLinkText2="Done"
                onLabelLinkPress2={
                  currentlyEditing === 'flagIds'
                    ? () => setCurrentlyEditing(undefined)
                    : undefined
                }
                explanation={
                  Object.keys(flags).length
                    ? 'Removing a flag will unset it in non-approved assessments'
                    : undefined
                }
              >
                {currentlyEditing === 'flagIds' ? (
                  <Card
                    expandedInline
                    expandedInlineFirst
                    expandedInlineLast
                    marginHorizontal={false}
                  >
                    {sortBy(Object.values(flags), 'ordering').map((item) => (
                      <CardRow
                        key={item.id}
                        label={item.name}
                        checkedValue={data.current.flagIds.includes(item.id)}
                        onPress={() => handleToggleFlag(item.id)}
                      />
                    ))}
                  </Card>
                ) : !Object.keys(flags).length ? (
                  <CardRow placeholder="No Flags Exist" />
                ) : (
                  <CardRow
                    placeholder={!flagNames ? 'No Flags Chosen' : ''}
                    label={flagNames}
                    labelNumberOfLines={999}
                    navigationType="input-radio"
                    onPress={() =>
                      animateNext(() => setCurrentlyEditing('flagIds'))
                    }
                  />
                )}
              </Card>

              <Card
                label="Signatories"
                labelLinkText="Manage Signatories"
                onLabelLinkPress={
                  currentlyEditing !== 'signatoryIds'
                    ? () =>
                        dispatch(
                          NAVIGATE_MODAL({
                            screen: ModalScreens.AseSetupSignatories,
                            params: {},
                          }),
                        )
                    : null
                }
                labelLinkText2="Done"
                onLabelLinkPress2={
                  currentlyEditing === 'signatoryIds'
                    ? () => setCurrentlyEditing(undefined)
                    : undefined
                }
              >
                {currentlyEditing === 'signatoryIds' ? (
                  <Card
                    expandedInline
                    expandedInlineFirst
                    expandedInlineLast
                    marginHorizontal={false}
                  >
                    {sortBy(Object.values(signatories), 'ordering').map(
                      (item) => (
                        <CardRow
                          key={item.id}
                          label={item.name}
                          checkedValue={data.current.signatoryIds.includes(
                            item.id,
                          )}
                          onPress={() => handleToggleSignatory(item.id)}
                        />
                      ),
                    )}
                  </Card>
                ) : !Object.keys(signatories).length ? (
                  <CardRow placeholder="No Signatories Exist" />
                ) : (
                  <CardRow
                    placeholder={!signatoryNames ? 'No Signatories Chosen' : ''}
                    label={signatoryNames}
                    labelNumberOfLines={999}
                    navigationType="input-radio"
                    onPress={() =>
                      animateNext(() => setCurrentlyEditing('signatoryIds'))
                    }
                  />
                )}
              </Card>

              <SeparatorHorizontal />

              <Card explanation="Choose where this template appears">
                <CardRow
                  label="Choose Organisations"
                  labelNumberOfLines={2}
                  checkedValue={data.current.limitOrganisations}
                  onPress={() => {
                    data.current.limitOrganisations =
                      !data.current.limitOrganisations;
                    forceUpdate();
                  }}
                />
                {data.current.limitOrganisations && (
                  <CardRow
                    labelNumberOfLines={0}
                    label={
                      visibleToOrganisationIds.length
                        ? allOrganisations
                            .filter((o) =>
                              visibleToOrganisationIds.includes(o.id),
                            )
                            .map((o) => o.name)
                            .join(', ')
                        : 'No Organisations Chosen'
                    }
                    icon={
                      visibleToOrganisationIds.length
                        ? undefined
                        : ICONS.CircleExclamationLight
                    }
                    iconColor={
                      visibleToOrganisationIds.length
                        ? undefined
                        : theme.colors.red
                    }
                    labelColor={
                      visibleToOrganisationIds.length
                        ? undefined
                        : theme.colors.red
                    }
                    onPress={() =>
                      dispatch(
                        NAVIGATE_MODAL({
                          screen: ModalScreens.SELECT,
                          params: {
                            eventKey: eventKeySelectOrganisation.current,
                            options: allOrganisations.map((o) => ({
                              name: o.name,
                              internalValue: o.id,
                            })),
                            multiSelect: true,
                            searchable: true,
                            title: 'Visible To',
                            optionNoun: 'Organisations',
                            value: visibleToOrganisationIds,
                          },
                        }),
                      )
                    }
                    navigationType="input-radio"
                  />
                )}
              </Card>

              <Card explanation="Period, from the start date, the assessment is valid">
                {currentlyEditing === 'validityPeriodId' ? (
                  <Card
                    marginHorizontal={false}
                    expandedInline
                    expandedInlineFirst
                    expandedInlineLast
                  >
                    {[
                      { id: null, name: 'Custom Timescale' },
                      ...sortBy(
                        Object.values(validityTimescales),
                        'ordering',
                      ).map((vt) => ({
                        id: vt.id,
                        name: vt.name,
                      })),
                    ].map((modifier) => (
                      <CardRow
                        key={modifier.id || -1}
                        label={modifier.name}
                        radioValue={
                          data.current.validityModifierId === modifier.id
                        }
                        onPress={() => {
                          data.current.validityModifierId = modifier.id;
                          setCurrentlyEditing(undefined);
                        }}
                      />
                    ))}
                  </Card>
                ) : !validityTimescale ? (
                  <ViewWithSeparators horizontal>
                    <CardRow
                      label="Validity Period"
                      onPress={() =>
                        animateNext(() =>
                          setCurrentlyEditing('validityPeriodId'),
                        )
                      }
                      style={{ flex: 1 }}
                    />
                    <CardRow
                      navigationIcon={ICONS.HourglassLight}
                      style={{
                        backgroundColor: 'white',
                      }}
                      contentStyle={{
                        paddingVertical: 0,
                        flexDirection: 'row',
                        flex: Platform.OS === 'web' ? undefined : 0,
                        width: 100,
                      }}
                      childrenStyle={{
                        width: 100,
                      }}
                    >
                      <TimescaleField
                        timescale={data.current.validityTimescale}
                        onChange={(newTimescale) => {
                          data.current.validityTimescale = newTimescale;
                          forceUpdate();
                        }}
                      />
                    </CardRow>
                  </ViewWithSeparators>
                ) : (
                  <CardRow
                    label="Validity Period"
                    value={validityTimescale.name}
                    navigationType="input-radio"
                    onPress={() =>
                      animateNext(() => setCurrentlyEditing('validityPeriodId'))
                    }
                  />
                )}
              </Card>

              <Card
                explanation={`An action due in "1 month" will have a due date of "${
                  actionTimescaleFromOptions[data.current.actionTimescaleFrom]
                    .name
                } + 1 month"`}
              >
                {currentlyEditing === 'actionTimescaleFrom' ? (
                  <Card
                    expandedInline
                    expandedInlineFirst
                    expandedInlineLast
                    marginHorizontal={false}
                  >
                    {Object.values(actionTimescaleFromOptions).map((atf) => (
                      <CardRow
                        key={atf.id}
                        label={atf.name}
                        radioValue={data.current.actionTimescaleFrom === atf.id}
                        onPress={() => {
                          data.current.actionTimescaleFrom = atf.id;
                          setCurrentlyEditing(undefined);
                        }}
                      />
                    ))}
                  </Card>
                ) : (
                  <CardRow
                    label="Action Timescale"
                    value={
                      actionTimescaleFromOptions[
                        data.current.actionTimescaleFrom
                      ].name
                    }
                    navigationType="input-radio"
                    onPress={() =>
                      animateNext(() =>
                        setCurrentlyEditing('actionTimescaleFrom'),
                      )
                    }
                  />
                )}
              </Card>

              <Card explanation="Allow more than one assessor per assessment">
                <CardRow
                  label="Multiple Assessors"
                  checkedValue={!!data.current.allowMultipleAssessors}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'allowMultipleAssessors',
                      !data.current.allowMultipleAssessors,
                    )
                  }
                />
              </Card>

              <Card explanation="Allow assessor to approve their own assessment even if they don't have the approve feature">
                <CardRow
                  label="Assessor Approve"
                  checkedValue={!!data.current.assessorApproval}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'assessorApproval',
                      !data.current.assessorApproval,
                    )
                  }
                />
              </Card>

              <Card explanation="Auto approve assessment when it's submitted. Assessor doesn't need the approve feature">
                <CardRow
                  label="Approve on Submit"
                  checkedValue={!!data.current.approveOnSubmit}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'approveOnSubmit',
                      !data.current.approveOnSubmit,
                    )
                  }
                />
              </Card>
              <Card explanation="Stop assessment from being submitted if some questions aren't answered">
                <CardRow
                  label="Require All Questions Be Answered"
                  checkedValue={!!data.current.requireAllAnswers}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'requireAllAnswers',
                      !data.current.requireAllAnswers,
                    )
                  }
                />
              </Card>
              <Card
                explanation={`Only users in ${organisation.name} can approve and unapprove these assessments`}
              >
                <CardRow
                  label={`Restrict approval to users in ${organisation.name}`}
                  checkedValue={!!data.current.ownerApprovalOnly}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'ownerApprovalOnly',
                      !data.current.ownerApprovalOnly,
                    )
                  }
                />
              </Card>
              <Card explanation="Allow assessors to add remarks to questions and checklists">
                <CardRow
                  label="Question and Checklist Remarks"
                  checkedValue={!!data.current.questionComments}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'questionComments',
                      !data.current.questionComments,
                    )
                  }
                />
              </Card>

              <Card
                label="Add Custom..."
                explanation="Assessors will still be able to add suggested actions and controls"
              >
                <ViewWithSeparators horizontal>
                  <CardRow
                    label="Actions"
                    checkedValue={!!data.current.addCustomActions}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'addCustomActions',
                        !data.current.addCustomActions,
                      )
                    }
                    style={{ flex: 1 }}
                  />
                  <CardRow
                    label="Controls"
                    checkedValue={!!data.current.addCustomControls}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'addCustomControls',
                        !data.current.addCustomControls,
                      )
                    }
                    style={{ flex: 1 }}
                  />
                </ViewWithSeparators>
              </Card>

              <Card
                label="Edit Asset Information"
                explanation="Edit an asset's general details from within an assessment"
                labelLinkText2="Done"
                onLabelLinkPress2={
                  currentlyEditing === 'editAsset'
                    ? () => setCurrentlyEditing(undefined)
                    : undefined
                }
              >
                {currentlyEditing === 'editAsset' ? (
                  <Card
                    expandedInline
                    expandedInlineFirst
                    expandedInlineLast
                    marginHorizontal={false}
                  >
                    <CardRow
                      label="Name"
                      checkedValue={data.current.editAssetName}
                      onPress={() =>
                        handleChangeWithNameWithForceUpdate(
                          'editAssetName',
                          !data.current.editAssetName,
                        )
                      }
                    />
                    <CardRow
                      label="Address"
                      checkedValue={data.current.editAssetAddress}
                      onPress={() =>
                        handleChangeWithNameWithForceUpdate(
                          'editAssetAddress',
                          !data.current.editAssetAddress,
                        )
                      }
                    />
                    <CardRow
                      label="Photo"
                      checkedValue={data.current.editAssetAvatar}
                      onPress={() =>
                        handleChangeWithNameWithForceUpdate(
                          'editAssetAvatar',
                          !data.current.editAssetAvatar,
                        )
                      }
                    />
                    <CardRow
                      label="Custom Reference"
                      checkedValue={data.current.editAssetCustomReference}
                      onPress={() =>
                        handleChangeWithNameWithForceUpdate(
                          'editAssetCustomReference',
                          !data.current.editAssetCustomReference,
                        )
                      }
                    />
                  </Card>
                ) : (
                  <CardRow
                    label={editAssetString}
                    placeholder={!editAssetString ? 'Cannot Edit' : ''}
                    labelNumberOfLines={999}
                    navigationType="input-radio"
                    onPress={() =>
                      animateNext(() => setCurrentlyEditing('editAsset'))
                    }
                  />
                )}
              </Card>
            </Gap>
          </ScrollView>
        </Freeze>
      )}

      {openedTabsRef.current.includes('pdf') && (
        <Freeze freeze={activeTab !== 'pdf'}>
          <ScrollView>
            <Gap>
              <Card>
                <CardRow
                  label="Page: Contents Page"
                  checkedValue={!!data.current.pdfContents}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'pdfContents',
                      !data.current.pdfContents,
                    )
                  }
                />
                <CardRow
                  label="Page: Summary / Certificate"
                  checkedValue={!!data.current.pdfIncludeCertificate}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'pdfIncludeCertificate',
                      !data.current.pdfIncludeCertificate,
                    )
                  }
                />
                <CardRow
                  label="Page: Photo Gallery"
                  checkedValue={!!data.current.pdfPagePhotos}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'pdfPagePhotos',
                      !data.current.pdfPagePhotos,
                    )
                  }
                />
              </Card>

              <Card label="Signatories">
                <CardRow
                  label="Page: Signatories"
                  checkedValue={!!data.current.pdfSignatories}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'pdfSignatories',
                      !data.current.pdfSignatories,
                    )
                  }
                />
                <TextField
                  placeholder="Signatories Title"
                  name="pdfSignatoriesTitle"
                  onChangeWithName={handleChangeWithName}
                  value={data.current.pdfSignatoriesTitle}
                  onEsc={handleClose}
                />
                <TextAreaField
                  placeholder="Signatories Introduction"
                  name="pdfSignatoriesText"
                  onChangeWithName={handleChangeWithName}
                  value={data.current.pdfSignatoriesText}
                  onEsc={handleClose}
                />
              </Card>

              <Card label="Action Plan">
                <OptionField
                  expandedInlineFirst
                  modalLabel="Action Plan"
                  value={[data.current.pdfActionPlan]}
                  onChange={handleChangeActionPlan}
                  options={[
                    { name: 'Action Plan Off', internalValue: null },
                    {
                      name: 'Action Plan Ordered by Due Date',
                      internalValue: 'dueDate2',
                    },
                    {
                      name: 'Action Plan Ordered by Severity',
                      internalValue: 'severity2',
                    },
                    {
                      name: 'Action Plan Ordered by Due Date (Plain)',
                      internalValue: 'dueDate',
                    },
                    {
                      name: 'Action Plan Ordered by Severity (Plain)',
                      internalValue: 'severity',
                    },
                  ]}
                />
                <TextAreaField
                  placeholder="Action Plan Introduction..."
                  name="pdfActionPlanText"
                  onChangeWithName={handleChangeWithName}
                  value={data.current.pdfActionPlanText}
                  onEsc={handleClose}
                />
              </Card>

              <GapSmall>
                <Card label="Actions & Controls">
                  <OptionField
                    onChange={(value) =>
                      handleChangeWithNameWithForceUpdate(
                        'pdfActionsControls',
                        value[0] ||
                          AseTemplateOptionPdfActionsControls.actionsControls,
                      )
                    }
                    value={[data.current.pdfActionsControls]}
                    modalLabel=""
                    options={[
                      {
                        name: 'Show Actions then Controls',
                        internalValue:
                          AseTemplateOptionPdfActionsControls.actionsControls,
                      },
                      {
                        name: 'Show Controls then Actions',
                        internalValue:
                          AseTemplateOptionPdfActionsControls.controlsActions,
                      },
                      {
                        name: 'Show Actions and Hide Controls',
                        internalValue:
                          AseTemplateOptionPdfActionsControls.actions,
                      },
                      {
                        name: 'Show Controls and Hide Actions',
                        internalValue:
                          AseTemplateOptionPdfActionsControls.controls,
                      },
                      {
                        name: 'Hide Actions and Controls',
                        internalValue: AseTemplateOptionPdfActionsControls.none,
                      },
                    ]}
                    expandedInlineFirst
                    expandedInlineLast
                  />
                </Card>

                <Card>
                  <CardRow
                    label="Show Action Comments & History"
                    checkedValue={!!data.current.showActionHistory}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'showActionHistory',
                        !data.current.showActionHistory,
                      )
                    }
                  />
                  <CardRow
                    label="Show Cancelled Actions"
                    checkedValue={!!data.current.showCancelledActions}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'showCancelledActions',
                        !data.current.showCancelledActions,
                      )
                    }
                  />
                  <CardRow
                    label="Show Action Severities"
                    checkedValue={!!data.current.pdfShowActionSeverities}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'pdfShowActionSeverities',
                        !data.current.pdfShowActionSeverities,
                      )
                    }
                  />
                  <CardRow
                    label="Show Action Timescales"
                    checkedValue={!!data.current.pdfShowActionTimescales}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'pdfShowActionTimescales',
                        !data.current.pdfShowActionTimescales,
                      )
                    }
                  />
                </Card>
                <Card>
                  <CardRow
                    label="Show Control Comments & History"
                    checkedValue={!!data.current.showControlHistory}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'showControlHistory',
                        !data.current.showControlHistory,
                      )
                    }
                  />
                  <CardRow
                    label="Show Cancelled Controls"
                    checkedValue={!!data.current.showCancelledControls}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'showCancelledControls',
                        !data.current.showCancelledControls,
                      )
                    }
                  />
                </Card>
              </GapSmall>

              <Card label="Checklists">
                <CardRow
                  label={smartText("Show 'Dash' Checklist Items")}
                  checkedValue={!!data.current.showDashChecklistItems}
                  onPress={() =>
                    handleChangeWithNameWithForceUpdate(
                      'showDashChecklistItems',
                      !data.current.showDashChecklistItems,
                    )
                  }
                />
              </Card>

              {isSupport && (
                <Card label="Support Options">
                  <CardRow
                    label="New Field Info Page"
                    checkedValue={!!data.current.pdfNewFieldInfoPage}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'pdfNewFieldInfoPage',
                        !data.current.pdfNewFieldInfoPage,
                      )
                    }
                    rbs
                  />
                  <CardRow
                    label="H1 Numbering"
                    checkedValue={!!data.current.pdfH1Numbering}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'pdfH1Numbering',
                        !data.current.pdfH1Numbering,
                      )
                    }
                    rbs
                  />
                  <CardRow
                    label="New Footer with Customer Logo"
                    checkedValue={!!data.current.pdfNewFooter}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'pdfNewFooter',
                        !data.current.pdfNewFooter,
                      )
                    }
                    rbs
                  />
                  <CardRow
                    label="New Font"
                    checkedValue={!!data.current.pdfNewFont}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'pdfNewFont',
                        !data.current.pdfNewFont,
                      )
                    }
                    rbs
                  />
                  <CardRow
                    label="Assessing Org Name & Address on Cover Page"
                    checkedValue={!!data.current.pdfCoverAssessingOrganisation}
                    onPress={() =>
                      handleChangeWithNameWithForceUpdate(
                        'pdfCoverAssessingOrganisation',
                        !data.current.pdfCoverAssessingOrganisation,
                      )
                    }
                    rbs
                  />
                </Card>
              )}

              <Card
                label="Approved Label"
                explanation={smartText(
                  'Override the "Approved" label on PDF reports. ' +
                    'The label should be past tense like "Approved" or "Validated"',
                )}
              >
                <TextAreaField
                  placeholder="Approved"
                  name="labelApproved"
                  onChangeWithName={handleChangeWithName}
                  value={data.current.labelApproved}
                  onEsc={handleClose}
                />
              </Card>

              <Card label="PDF Introduction">
                <TextAreaField
                  placeholder="Introduction text..."
                  name="pdfIntroduction"
                  onChangeWithName={handleChangeWithName}
                  value={data.current.pdfIntroduction}
                  onEsc={handleClose}
                />
              </Card>

              {isSupport && (
                <Card label="PDF Body">
                  <TextAreaField
                    rbs
                    placeholder="Body text..."
                    name="pdfBody"
                    onChangeWithName={handleChangeWithName}
                    value={data.current.pdfBody}
                    onEsc={handleClose}
                  />
                </Card>
              )}

              <Card label="PDF Appendix">
                <TextAreaField
                  placeholder="Appendix text..."
                  name="pdfAppendix"
                  onChangeWithName={handleChangeWithName}
                  value={data.current.pdfAppendix}
                  onEsc={handleClose}
                />
              </Card>

              <Card label="PDF Colours">
                <ViewWithSeparators horizontal style={{ flex: 1 }}>
                  <CardRow
                    label="Header 1"
                    icon={
                      !data.current.colourHeader1 ||
                      colourRegExp.test(
                        colourWithHash(data.current.colourHeader1),
                      )
                        ? ICONS.CircleSolid
                        : ICONS.CircleLight
                    }
                    iconColor={returnValidColour([
                      data.current.colourHeader1,
                      '#000000',
                    ])}
                    style={{ width: 130 }}
                  />
                  <TextField
                    placeholder="#000000"
                    containerStyle={{ flex: 1 }}
                    name="colourHeader1"
                    value={colourWithHash(data.current.colourHeader1)}
                    onChangeWithName={handleColourChange}
                  />
                </ViewWithSeparators>
                <ViewWithSeparators horizontal style={{ flex: 1 }}>
                  <CardRow
                    label="Header 2"
                    style={{ width: 130 }}
                    icon={
                      !data.current.colourHeader2 ||
                      colourRegExp.test(
                        colourWithHash(data.current.colourHeader2),
                      )
                        ? ICONS.CircleSolid
                        : ICONS.CircleLight
                    }
                    iconColor={returnValidColour([
                      data.current.colourHeader2,
                      '#000000',
                    ])}
                  />
                  <TextField
                    placeholder="#000000"
                    containerStyle={{ flex: 1 }}
                    name="colourHeader2"
                    value={colourWithHash(data.current.colourHeader2)}
                    onChangeWithName={handleColourChange}
                  />
                </ViewWithSeparators>
                <ViewWithSeparators horizontal style={{ flex: 1 }}>
                  <CardRow
                    label="Header 3"
                    style={{ width: 130 }}
                    icon={
                      !data.current.colourHeader3 ||
                      colourRegExp.test(
                        colourWithHash(data.current.colourHeader3),
                      )
                        ? ICONS.CircleSolid
                        : ICONS.CircleLight
                    }
                    iconColor={returnValidColour([
                      data.current.colourHeader3,
                      '#000000',
                    ])}
                  />
                  <TextField
                    placeholder="#000000"
                    containerStyle={{ flex: 1 }}
                    name="colourHeader3"
                    value={colourWithHash(data.current.colourHeader3)}
                    onChangeWithName={handleColourChange}
                  />
                </ViewWithSeparators>
                <ViewWithSeparators horizontal style={{ flex: 1 }}>
                  <CardRow
                    label="Cover"
                    style={{ width: 130 }}
                    icon={
                      !data.current.colourHeaderCover ||
                      colourRegExp.test(
                        colourWithHash(data.current.colourHeaderCover),
                      )
                        ? ICONS.CircleSolid
                        : ICONS.CircleLight
                    }
                    iconColor={returnValidColour([
                      data.current.colourHeaderCover,
                      '#000000',
                    ])}
                  />
                  <TextField
                    placeholder="#000000"
                    containerStyle={{ flex: 1 }}
                    name="colourHeaderCover"
                    value={colourWithHash(data.current.colourHeaderCover)}
                    onChangeWithName={handleColourChange}
                  />
                </ViewWithSeparators>
              </Card>

              <Card label="Custom PDF Designs">
                {currentlyEditing === 'pdfId' ? (
                  <Card
                    expandedInline
                    expandedInlineFirst
                    marginHorizontal={false}
                  >
                    {sortBy(Object.values(pdfs), 'name').map((p) => (
                      <CardRow
                        key={p.id}
                        label={p.name}
                        radioValue={data.current.pdfId === p.id}
                        onPress={() => {
                          data.current.pdfId = p.id;
                          setCurrentlyEditing(undefined);
                        }}
                      />
                    ))}
                  </Card>
                ) : (
                  <CardRow
                    label={pdf?.name || 'Unknown'}
                    navigationType="input-radio"
                    onPress={() =>
                      animateNext(() => setCurrentlyEditing('pdfId'))
                    }
                  />
                )}
              </Card>
            </Gap>
          </ScrollView>
        </Freeze>
      )}

      {isSupport && (
        <Freeze freeze={activeTab !== 'pricing'}>
          <ScrollView>
            <Gap>
              <CostsScreen
                costs={data.current.costs}
                defaultCost={templateDefaultCosts?.cost ?? 0}
                defaultMinCost={templateDefaultCosts?.minCost ?? 0}
                defaultMaxCost={templateDefaultCosts?.maxCost ?? null}
                onChange={handleChangeCost}
                onClose={handleClose}
              />
            </Gap>
          </ScrollView>
        </Freeze>
      )}
    </Page>
  );
};
